import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// Extend dayjs with timezone support
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Format timestamp to ISO 8601 start of day UTC (e.g. "2025-02-03T00:00:00.000Z")
 * Use for dateFrom when API expects ISO 8601.
 */
export function formatToISO8601StartOfDay(timestamp: number): string {
  try {
    const d = new Date(timestamp)
    const year = d.getUTCFullYear()
    const month = d.getUTCMonth()
    const day = d.getUTCDate()
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString()
  } catch {
    return ''
  }
}

/**
 * Format timestamp to ISO 8601 end of day UTC (e.g. "2025-02-14T23:59:59.999Z")
 * Use for dateTo when API expects ISO 8601.
 */
export function formatToISO8601EndOfDay(timestamp: number): string {
  try {
    const d = new Date(timestamp)
    const year = d.getUTCFullYear()
    const month = d.getUTCMonth()
    const day = d.getUTCDate()
    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString()
  } catch {
    return ''
  }
}

/**
 * Format timestamp to YYYY-MM-DD (e.g. "2023-01-01")
 */
export function formatToYYYYMMDD(timestamp: number): string {
  try {
    const d = new Date(timestamp)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  } catch {
    return '--'
  }
}

/**
 * Format date for display (e.g. "30 Jan 2026" - day month year)
 */
export function formatDate(dateString: string | undefined): string {
  if (!dateString) return '--'
  try {
    return dayjs(dateString).format('D MMM YYYY')
  } catch {
    return dateString
  }
}

export function convertToRelativeTime(date: string): string {
  if (!date) return ''

  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)

  let interval = seconds / 31536000
  if (interval > 1) return Math.floor(interval) + ' years ago'

  interval = seconds / 2592000
  if (interval > 1) return Math.floor(interval) + ' months ago'

  interval = seconds / 86400
  if (interval > 1) return Math.floor(interval) + ' days ago'

  interval = seconds / 3600
  if (interval > 1) return Math.floor(interval) + ' hours ago'

  interval = seconds / 60
  if (interval > 1) return Math.floor(interval) + ' minutes ago'

  return Math.floor(seconds) + ' seconds ago'
}

export function convertToExactDateTime(date: string, timezone?: string): string {
  return dayjs(date).tz(timezone).format('dddd / DD MMM YYYY / hh:mm A')
}

export function convertToDateTime(date: string, timezone?: string): string {
  return dayjs(date).tz(timezone).format('DD/MM/YYYY hh:mm A')
}

/**
 * Format event times with proper timezone handling
 * @param startTime - Event start time (ISO string)
 * @param endTime - Event end time (ISO string)
 * @param userTimezone - User's timezone (optional, defaults to browser timezone)
 * @returns Formatted event times with date, from time, and to time
 */
export function formatEventTimes(startTime: string, endTime: string, userTimezone?: string) {
  // Use provided timezone or guess from browser
  const timezone = userTimezone || dayjs.tz.guess()

  // Convert to dayjs objects in user's timezone
  const startMoment = dayjs(startTime).tz(timezone)
  let endMoment = dayjs(endTime).tz(timezone)

  // Handle case where end time is before start time (same day event)
  if (endMoment.isBefore(startMoment)) {
    endMoment = endMoment.date(startMoment.date())
  }

  // Format the date
  const formattedDate = startMoment.format('D MMM YYYY')

  // Format times
  const fromTime = startMoment.format('h:mm A')
  const toTime = endMoment.format('h:mm A')

  return {
    date: formattedDate,
    fromTime: fromTime,
    toTime: toTime,
  }
}

export function convertToCompactTime(isoDate: string, addAgo: boolean = true): string {
  // Get the current date and time
  const currentDate = new Date()

  // Convert the ISO date string to Date
  const givenDate = new Date(isoDate)

  // Calculate the time difference in milliseconds
  const timeDifference = currentDate.getTime() - givenDate.getTime()

  // Convert the time difference to seconds
  const seconds = timeDifference / 1000

  // Define the time intervals
  const intervals: [string, number][] = [
    ['y', 31536000], // seconds in a year
    ['mo', 2592000], // seconds in a month
    ['w', 604800], // seconds in a week
    ['d', 86400], // seconds in a day
    ['h', 3600], // seconds in an hour
    ['m', 60], // seconds in a minute
    ['s', 1], // seconds
  ]

  // Generate the relative time expression
  let output = ''
  for (const [intervalName, intervalSeconds] of intervals) {
    if (seconds < 1) {
      output = 'Just now'
      break
    }
    if (seconds >= intervalSeconds) {
      const intervalValue = Math.floor(seconds / intervalSeconds)
      output += `${intervalValue}${intervalName}${addAgo ? ' ago' : ''}`
      break
    }
  }

  return output
}
